import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/client';

// Ported from primus-admin-main's BenchmarkModal.jsx, dispatching through the
// cross-café internal endpoints instead of the café-scoped /api/command/*
// ones. Difference: no SSE command.ack subscription (this app has no
// eventStream utility) -- the poll fallback (already self-sufficient with
// its own timeout in the original) is the only delivery path here, so it's
// used unconditionally rather than as a fallback.

const OVERLAY = { position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const CARD = { background: '#111827', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 20, padding: '24px 26px', width: '100%', maxWidth: 620, maxHeight: '88vh', overflowY: 'auto', color: '#fff' };
const GIB = 1024 * 1024 * 1024;

const gb = (bytes) => (bytes && bytes > 0 ? (bytes / GIB).toFixed(1) : '0');
const gbL = (bytes) => `${gb(bytes)} GB`;
const num = (n, d = 0) => (typeof n === 'number' && !Number.isNaN(n) ? n.toLocaleString(undefined, { maximumFractionDigits: d }) : '—');
const rate = (bps) => (bps >= 1024 * 1024 ? `${(bps / (1024 * 1024)).toFixed(1)} MB/s` : `${Math.max(0, Math.round(bps / 1024))} KB/s`);
const clamp = (p) => Math.max(0, Math.min(100, p || 0));
const usageColor = (p) => (p >= 85 ? '#ef4444' : p >= 60 ? '#f59e0b' : '#22c55e');

const Bar = ({ percent, color }) => (
    <div style={{ height: 8, borderRadius: 5, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
        <div style={{ width: `${clamp(percent)}%`, height: '100%', background: color || usageColor(clamp(percent)), transition: 'width .3s' }} />
    </div>
);

const UsageRow = ({ label, percent, value, color }) => (
    <div style={{ marginBottom: 13 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
            <span style={{ color: '#cbd1da' }}>{label}</span>
            <span style={{ fontWeight: 700 }}>{value}</span>
        </div>
        <Bar percent={percent} color={color} />
    </div>
);

const MetricRow = ({ name, value, desc }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{name}</div>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2, lineHeight: 1.4 }}>{desc}</div>
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, whiteSpace: 'nowrap', textAlign: 'right' }}>{value}</div>
    </div>
);

const DetailRow = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', fontSize: 13, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <span style={{ color: '#9CA3AF' }}>{label}</span>
        <span style={{ textAlign: 'right', fontWeight: 600 }}>{value}</span>
    </div>
);

const SectionTitle = ({ children, hint }) => (
    <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: '#e5e7eb' }}>{children}</div>
        {hint && <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4, lineHeight: 1.45 }}>{hint}</div>}
    </div>
);

export default function BenchmarkModal({ pc, cafeId, onClose }) {
    const liveMode = !!pc.current_user_id;
    const [phase, setPhase] = useState('starting');
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const commandIdRef = useRef(null);
    const [runToken, setRunToken] = useState(0);

    const start = useCallback(async () => {
        setPhase('starting');
        setResult(null);
        setError(null);
        commandIdRef.current = null;
        try {
            const r = await api.post(`/internal/pcs/${cafeId}/command`, { pc_id: pc.id, command: 'benchmark', params: null });
            commandIdRef.current = r.data?.id ?? null;
            setPhase('running');
        } catch (e) {
            setError(e.response?.data?.detail
                || (e.response?.status === 409 ? 'The PC can\'t run a benchmark right now.' : 'Failed to start benchmark.'));
            setPhase('error');
        }
    }, [pc.id, cafeId]);

    useEffect(() => { start(); }, [start, runToken]);

    // Poll for the result (this app has no SSE command.ack stream, so the
    // poll is the only delivery path, not just a fallback).
    useEffect(() => {
        if (phase !== 'running') return undefined;
        let stop = false;
        const startedAt = Date.now();
        const poll = async () => {
            try {
                const r = await api.get(`/internal/pcs/${cafeId}/history/${pc.id}`);
                const cmd = (r.data || []).find((c) => c.id === commandIdRef.current);
                if (cmd && !stop) {
                    if (cmd.state === 'SUCCEEDED') { setResult(cmd.result || {}); setPhase('done'); return; }
                    if (cmd.state === 'FAILED') { setError(cmd.result?.reason || 'Benchmark failed on the PC.'); setPhase('error'); return; }
                    if (cmd.state === 'EXPIRED') { setError('The kiosk did not respond (command expired).'); setPhase('error'); return; }
                }
            } catch { /* ignore, retry next tick */ }
            if (!stop && Date.now() - startedAt > 150000) {
                setError('Timed out waiting for the PC to report results.');
                setPhase('error');
            }
        };
        poll();
        const id = setInterval(poll, 3000);
        return () => { stop = true; clearInterval(id); };
    }, [phase, pc.id, cafeId]);

    const sys = result?.system;
    const cpu = result?.cpu;
    const mem = result?.memory;
    const disk = result?.disk;
    const gpu = result?.gpu;
    const load = result?.load_snapshot;
    const procs = Array.isArray(result?.top_processes) ? result.top_processes : [];
    const cpuProcs = Array.isArray(result?.cpu_consumers) ? result.cpu_consumers : [];
    const diskProcs = Array.isArray(result?.disk_consumers) ? result.disk_consumers : [];
    const maxDiskRate = diskProcs.reduce((m, p) => Math.max(m, p.disk_bytes_per_sec || 0), 0);

    const ramTotal = load?.ram_total_bytes || sys?.total_ram_bytes || 0;
    const diskUsedPct = sys?.disk && sys.disk.size_bytes > 0
        ? ((sys.disk.size_bytes - sys.disk.free_bytes) / sys.disk.size_bytes) * 100
        : 0;

    return (
        <div style={OVERLAY} onClick={onClose}>
            <div style={CARD} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700 }}>Benchmark — {pc.name}</h2>
                        <div style={{ fontSize: 13, color: '#9CA3AF', marginTop: 2 }}>Performance &amp; health check for this PC</div>
                    </div>
                    <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#9CA3AF', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}>×</button>
                </div>

                {liveMode && (
                    <div style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 12, padding: '10px 12px', margin: '10px 0 4px', color: '#fcd34d', fontSize: 13, lineHeight: 1.45 }}>
                        A customer is signed in and may be playing. Running in <strong>live mode</strong> — hardware details and current load only. The speed/stress tests are skipped so the game isn&apos;t disturbed.
                    </div>
                )}

                {(phase === 'starting' || phase === 'running') && (
                    <div style={{ padding: '34px 0', textAlign: 'center', color: '#9CA3AF' }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{liveMode ? 'Reading the PC…' : 'Running the test…'}</div>
                        <div style={{ fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>
                            {liveMode
                                ? <>Reading the PC&apos;s hardware details and current load.<br />This takes a few seconds and won&apos;t affect the game.</>
                                : <>The PC is measuring its processor, memory, disk and graphics.<br />This takes about 20–40 seconds.</>}
                        </div>
                    </div>
                )}

                {phase === 'error' && (
                    <div style={{ padding: '22px 0' }}>
                        <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 12, padding: 14, color: '#fca5a5', fontSize: 14 }}>
                            {error}
                        </div>
                    </div>
                )}

                {phase === 'done' && result && (
                    <div>
                        <SectionTitle hint="How fast this PC performed. Use these to compare PCs — higher numbers are better.">Performance</SectionTitle>
                        <div style={{ marginTop: 8 }}>
                            {cpu && <MetricRow name="Processor (all cores)" value={num(cpu.score)} desc="Overall speed using every core. The main number for comparing PCs." />}
                            {cpu && <MetricRow name="Processor (single core)" value={`${num(cpu.single_thread_mops, 1)} Mops`} desc="Speed of one core — matters for older apps and games." />}
                            {mem && <MetricRow name="Memory speed" value={`${num(mem.bandwidth_mb_s)} MB/s`} desc="How fast data moves in and out of RAM." />}
                            {disk && <MetricRow name="Disk — read" value={`${num(disk.seq_read_mb_s)} MB/s`} desc="Speed loading large files (games, videos)." />}
                            {disk && <MetricRow name="Disk — write" value={`${num(disk.seq_write_mb_s)} MB/s`} desc="Speed saving large files." />}
                            {disk && <MetricRow name="Disk — small files" value={`${num(disk.random_read_mb_s)} MB/s`} desc="Tiny scattered reads — affects how fast apps open." />}
                            {gpu && <MetricRow name="Graphics (2D)" value={gpu.measured ? `${num(gpu.render_fps_approx)} fps` : 'not measured'} desc="Rough 2D drawing speed. This is not a 3D game frame-rate." />}
                        </div>

                        {load && (
                            <>
                                <SectionTitle hint="Measured just before the test, so it shows the PC's normal load from other apps — the benchmark itself is not counted.">Usage before the test</SectionTitle>
                                <div style={{ marginTop: 10 }}>
                                    <UsageRow label="Processor load" percent={load.cpu_percent} value={`${num(load.cpu_percent)}%`} />
                                    <UsageRow
                                        label="Memory in use"
                                        percent={load.ram_percent}
                                        value={ramTotal ? `${gbL(load.ram_used_bytes)} of ${gbL(ramTotal)} (${num(load.ram_percent)}%)` : `${num(load.ram_percent)}%`}
                                    />
                                    {load.gpu_percent != null && <UsageRow label="Graphics load" percent={load.gpu_percent} value={`${num(load.gpu_percent)}%`} />}
                                    {sys?.disk && sys.disk.size_bytes > 0 && (
                                        <UsageRow label="Disk space used" percent={diskUsedPct} value={`${gbL(sys.disk.free_bytes)} free of ${gbL(sys.disk.size_bytes)}`} />
                                    )}
                                    {load.cpu_temp_c != null && (
                                        <div style={{ fontSize: 13, color: '#cbd1da', marginTop: 2 }}>Processor temperature: <strong>{num(load.cpu_temp_c)} °C</strong></div>
                                    )}
                                </div>
                            </>
                        )}

                        {procs.length > 0 && ramTotal > 0 && (
                            <>
                                <SectionTitle hint="The apps using the most memory. This is usually why memory looks high.">What&apos;s using the memory</SectionTitle>
                                <div style={{ marginTop: 10 }}>
                                    {procs.map((p, i) => (
                                        <UsageRow key={i} label={p.name} percent={(p.ram_bytes / ramTotal) * 100} value={gbL(p.ram_bytes)} color="#60a5fa" />
                                    ))}
                                </div>
                            </>
                        )}

                        {cpuProcs.length > 0 && (
                            <>
                                <SectionTitle hint="The apps using the most processor power.">What&apos;s using the processor</SectionTitle>
                                <div style={{ marginTop: 10 }}>
                                    {cpuProcs.map((p, i) => (
                                        <UsageRow key={i} label={p.name} percent={p.cpu_percent} value={`${num(p.cpu_percent, 1)}%`} color="#a78bfa" />
                                    ))}
                                </div>
                            </>
                        )}

                        {diskProcs.length > 0 && (
                            <>
                                <SectionTitle hint="The apps reading and writing to the disk the most.">What&apos;s using the disk</SectionTitle>
                                <div style={{ marginTop: 10 }}>
                                    {diskProcs.map((p, i) => (
                                        <UsageRow key={i} label={p.name} percent={maxDiskRate ? (p.disk_bytes_per_sec / maxDiskRate) * 100 : 0} value={rate(p.disk_bytes_per_sec)} color="#34d399" />
                                    ))}
                                </div>
                            </>
                        )}

                        {sys && (
                            <>
                                <SectionTitle>System details</SectionTitle>
                                <div style={{ marginTop: 8 }}>
                                    <DetailRow label="Processor" value={`${sys.cpu_name || '—'}${sys.cpu_cores ? ` · ${sys.cpu_cores} cores / ${sys.cpu_threads} threads` : ''}${sys.cpu_clock_mhz ? ` · ${(sys.cpu_clock_mhz / 1000).toFixed(1)} GHz` : ''}`} />
                                    <DetailRow label="Memory" value={`${gbL(sys.total_ram_bytes)}${sys.ram_speed_mhz ? ` · ${sys.ram_speed_mhz} MHz` : ''}`} />
                                    {(sys.gpus || []).map((g, i) => (
                                        <DetailRow key={i} label={(sys.gpus.length > 1 ? `Graphics ${i + 1}` : 'Graphics')} value={`${g.name}${g.vram_bytes ? ` · ${gbL(g.vram_bytes)}` : ''}`} />
                                    ))}
                                    {sys.disk && <DetailRow label="Storage" value={`${sys.disk.model} · ${sys.disk.type} · ${gbL(sys.disk.free_bytes)} free of ${gbL(sys.disk.size_bytes)}`} />}
                                    <DetailRow label="Operating system" value={sys.os || '—'} />
                                    {typeof result.duration_ms === 'number' && <DetailRow label="Test duration" value={`${(result.duration_ms / 1000).toFixed(1)} s`} />}
                                    {Array.isArray(result.tests_run) && result.tests_run.length > 0 && <DetailRow label="Tests run" value={result.tests_run.join(', ')} />}
                                </div>
                            </>
                        )}

                        {Array.isArray(result.errors) && result.errors.length > 0 && (
                            <div style={{ marginTop: 14, fontSize: 12, color: '#fca5a5' }}>
                                Some tests didn&apos;t run: {result.errors.join('; ')}
                            </div>
                        )}
                    </div>
                )}

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 22, position: 'sticky', bottom: 0, background: '#111827', paddingTop: 12 }}>
                    {(phase === 'done' || phase === 'error') && (
                        <button
                            type="button"
                            onClick={() => setRunToken((t) => t + 1)}
                            style={{ padding: '9px 20px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.2)', background: 'transparent', color: '#fff', cursor: 'pointer', fontSize: 14 }}
                        >Run again</button>
                    )}
                    <button type="button" onClick={onClose} className="btn btn--primary">Close</button>
                </div>
            </div>
        </div>
    );
}
